import { useState, useEffect } from 'react';

interface Breakpoints {
  mobile: string;
  tablet: string;
  desktop: string;
}

type MatchState = Record<string, boolean>;

const breakpoints: Breakpoints = {
  mobile: '(max-width: 767px)',
  tablet: '(min-width: 768px) and (max-width: 991px)',
  desktop: '(min-width: 992px)',
};

export const useMedia = () => {
  const [matches, setMatches] = useState<MatchState>(() => {
    if (typeof window === 'undefined') return {};

    return Object.keys(breakpoints).reduce<MatchState>((acc, key) => {
      acc[key] = window.matchMedia(breakpoints[key as keyof Breakpoints]).matches;
      return acc;
    }, {});
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQueryLists = Object.keys(breakpoints).map(key => ({
      key,
      mql: window.matchMedia(breakpoints[key as keyof Breakpoints]),
    }));

    const handleChange = () => {
      setMatches(
        mediaQueryLists.reduce<MatchState>((acc, { key, mql }) => {
          acc[key] = mql.matches;
          return acc;
        }, {})
      );
    };

    mediaQueryLists.forEach(({ mql }) => {
      mql.addEventListener('change', handleChange);
    });

    return () => {
      mediaQueryLists.forEach(({ mql }) => {
        mql.removeEventListener('change', handleChange);
      });
    };
  }, []);

  return {
    isMobile: matches.mobile || false,
    isTablet: matches.tablet || false,
    isDesktop: matches.desktop || false,
    isJumbo: matches.jumbo || false,
    matches,
  };
};
